import { useEffect, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { EnvVars } from '../../../../../environment';
import { APIBaseChronos } from '@/api/hosts';
import relativityLogo from '@/assets/relativity.png';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';

interface RelativityConfig {
  site: string;
  clientId: string;
  clientSecret: string;
}

export function Integrations() {
  const { user } = useUserContext();
  const authInfo = useAuthInfo();
  const { hasPermission } = useHasPermission();
  const navigate = useNavigate();

  // Extract just the subdomain from the full URL
  const extractSubdomain = (url: string) => {
    if (!url) return '';
    const match = url.match(/https?:\/\/([^.]+)\.relativity\.one/);
    return match ? match[1] : url;
  };

  const [config, setConfig] = useState<RelativityConfig>({
    site: extractSubdomain(user?.organisation.relativitySite || ''),
    clientId: user?.organisation.relativityClientId || '',
    clientSecret: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditingSecret, setIsEditingSecret] = useState(false);
  const hasExistingSecret = Boolean(user?.organisation.relativityClientSecretEncrypted);

  useEffect(() => {
    if (user?.organisation) {
      setConfig((prev) => ({
        ...prev,
        site: extractSubdomain(user.organisation.relativitySite || ''),
        clientId: user.organisation.relativityClientId || '',
      }));
    }
  }, [user?.organisation]);

  const hasUnsavedChanges = user?.organisation
    ? config.site !== extractSubdomain(user.organisation.relativitySite || '') ||
      config.clientId !== (user.organisation.relativityClientId || '') ||
      isEditingSecret
    : false;

  const getConfigurationStatus = () => {
    if (!user?.organisation) {
      return null;
    }

    const hasSavedConfig =
      hasExistingSecret && user.organisation.relativitySite && user.organisation.relativityClientId;

    if (!hasSavedConfig) {
      return 'not-set-up';
    }

    if (hasUnsavedChanges) {
      return 'not-saved';
    }

    return 'saved';
  };

  const configStatus = getConfigurationStatus();

  const handleInputChange = (field: keyof RelativityConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    setIsLoading(true);
    try {
      const token = await authInfo.tokens.getAccessToken();
      const response = await fetch(`${APIBaseChronos}/api/org/relativity-config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          relativitySite: `https://${config.site}.relativity.one`,
          relativityClientId: config.clientId,
          relativityClientSecret: config.clientSecret || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      toast.success('Relativity configuration saved successfully');

      // Clear the client secret field after successful save and exit edit mode
      setConfig((prev) => ({ ...prev, clientSecret: '' }));
      setIsEditingSecret(false);
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasPermission(Permission.IntegrationsManage)) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-gray-500">
          You do not have permission to manage integrations. Please contact your administrator or the Wexler team.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center">
          <img src={relativityLogo} alt="Relativity" className="h-10 w-10" />
        </div>
        <div>
          <h2 className="font-serif text-base font-semibold text-gray-800">Relativity Integration</h2>
          <p className="text-sm text-gray-600">Connect to your Relativity instance for document import</p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="site">Relativity Site URL</Label>
            <div className="flex h-10 items-center rounded-md border border-gray-300 bg-white">
              <span className="px-3 py-2 text-sm text-gray-500">https://</span>
              <Input
                id="site"
                type="text"
                placeholder="your-site"
                value={config.site}
                onChange={(e) => handleInputChange('site', e.target.value)}
                className="h-full border-0 px-0 py-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <span className="px-3 py-2 text-sm text-gray-500">.relativity.one</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clientId">Client ID</Label>
            <Input
              id="clientId"
              type="text"
              placeholder="Enter your Relativity client ID"
              value={config.clientId}
              onChange={(e) => handleInputChange('clientId', e.target.value)}
            />
          </div>
        </div>

        <div className="w-1/2 space-y-2">
          <Label htmlFor="clientSecret">Client Secret</Label>
          {hasExistingSecret && !isEditingSecret ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 flex-1 items-center rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
                  <code className="text-sm">{user?.organisation.relativityClientSecretInitialCharacters}</code>
                  <code className="text-gray-400">{Array(36).fill('*').join('')}</code>
                </div>
                <Button onClick={() => setIsEditingSecret(true)}>
                  <Icon className="lucide--pencil-line" />
                  Edit
                </Button>
              </div>
            </div>
          ) : (
            <Input
              id="clientSecret"
              type="password"
              placeholder="Enter your Relativity client secret"
              value={config.clientSecret}
              onChange={(e) => handleInputChange('clientSecret', e.target.value)}
            />
          )}
        </div>

        <div className="mt-8 flex items-center justify-between border-t border-gray-200 pt-6">
          <div className="text-sm text-gray-500">
            {configStatus === 'saved' && (
              <span className="inline-flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                Configuration saved
              </span>
            )}
            {configStatus === 'not-saved' && (
              <span className="inline-flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                Configuration not saved
              </span>
            )}
            {configStatus === 'not-set-up' && (
              <span className="inline-flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                Configuration not set up
              </span>
            )}
            {configStatus === null && <span></span>}
          </div>

          <div className="flex gap-2">
            {configStatus === 'saved' && (
              <Button onClick={() => navigate('/relativity/field-mappings')}>
                <Icon className="lucide--settings-2" />
                Configure Field Mappings
              </Button>
            )}

            <Button
              variant="primary"
              onClick={handleSaveConfig}
              disabled={
                isLoading ||
                !config.site ||
                !config.clientId ||
                (!hasExistingSecret && !isEditingSecret && !config.clientSecret) ||
                (isEditingSecret && !config.clientSecret)
              }
            >
              <Icon className="lucide--save" />
              {isLoading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-blue-100 bg-blue-50/50 p-4">
        <h3 className="mb-4 border-b border-blue-100 font-medium text-blue-900">Setup Instructions</h3>
        <ol className="list-inside list-decimal space-y-1 text-sm text-blue-800">
          <li>Contact your Relativity administrator to create a OAuth2 client with these steps</li>
          <li>Create a new OAuth2 client with the following settings:</li>
          <ol className="list-inside list-disc pl-5">
            <li>Flow Grant Type: Code</li>
            <li>
              Redirect URL:{' '}
              <code className="rounded border border-blue-300 bg-blue-200 px-1">
                {EnvVars.API_URL}/oauth/relativity
              </code>
            </li>
            <li>Access Token Lifetime: 60 minutes</li>
            <li>Refresh Tokens: Enabled</li>
          </ol>
          <li>Enter your Relativity site URL, client ID, and client secret above</li>
          <li>Save the configuration to enable Relativity document import</li>
        </ol>
      </div>
    </div>
  );
}
